import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, takeUntil, filter, map } from 'rxjs/operators';
import { AuthenticationService } from '@bo-client/core';
import { Subject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { ChangePasswordComponent } from '@bo-client/shared/login/change-password/change-password.component';
import { RegexToCheckEMail, RegexToCheckPassword } from '@bo-client/shared/forms/forms.validators';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DataShareService } from '../../services/data-share.service';

@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss']
})
export class LoginDialogComponent implements OnInit, OnDestroy {
  formGroup: FormGroup;

  public hide = true;
  public isLoading = false;
  public error: string;
  private compUnsubs$ = new Subject<void>();
  showExternalIdpLogin$: Observable<boolean>;
  hideLocalLogin$: Observable<boolean>;
  externalIdpButtonText$: Observable<string>;
  externalIdpDescription$: Observable<string>;

  // Add this property to make window available to the template
  window = window;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,

    public matDialog: MatDialog,
    public dialogRef: MatDialogRef<LoginDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public isMainLogin: boolean,
    public validatorsService: ValidatorsService,
    private oidcSecurityService: OidcSecurityService,
    private authorizationService: AuthorizationService,
    private dataShareService: DataShareService
  ) {
    this.hideLocalLogin$ = this.dataShareService.tenantConfig$.pipe(
      filter((config) => !!config),
      map((config) => config?.iamConfig?.hideLocalLogin)
    );
    this.showExternalIdpLogin$ = this.dataShareService.tenantConfig$.pipe(
      filter((config) => !!config),
      map((config) => config?.iamConfig?.externalIdpEnabled)
    );
    this.externalIdpButtonText$ = this.dataShareService.tenantConfig$.pipe(
      filter((config) => !!config),
      map((config) => config?.iamConfig?.loginWithButtonText)
    );
    this.externalIdpDescription$ = this.dataShareService.tenantConfig$.pipe(
      filter((config) => !!config),
      map((config) => config?.iamConfig?.loginWithDescription)
    );
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.formGroup = this.formBuilder.group({
      username: [null, [Validators.required, Validators.pattern(RegexToCheckEMail)]],
      password: [null, [Validators.required, Validators.pattern(RegexToCheckPassword)]],
      remember: [true]
    });
  }

  get email() {
    return this.formGroup.get('username') as FormControl;
  }

  get password() {
    return this.formGroup.get('password') as FormControl;
  }

  logIn() {
    this.isLoading = true;
    this.authenticationService
      .login(this.formGroup.value)
      .pipe(
        finalize(() => {
          this.formGroup.markAsPristine();
        }),
        takeUntil(this.compUnsubs$)
      )
      .subscribe({
        next: (loggedInUser) => {
          this.isLoading = false;
          this.router.navigate([this.authorizationService.selectRouteLogin()], {
            replaceUrl: true
          });
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.isLoading = false;
          this.error = error;
        }
      });
  }

  forgotPassword() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'forgot-password-dialog';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      isLink: false,
      isUserMail: true,
      isPassword: false
    };
    this.dialogRef.close(false);
    const modalDialog = this.matDialog
      .open(ChangePasswordComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((result) => {});
  }

  closeModal() {
    this.dialogRef.close(false);
  }

  loginWithExternalIDP() {
    console.log('Calling authorize with external-idp');
    this.oidcSecurityService.authorize('external-idp');
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
